import {HelpElement, UIElement} from "../../common/ui/ui";
import {Panel} from "../../common/ui/Panel";

/**
 * Any tool
 */
class Tool extends UIElement {
    /**
     *
     * @param config {ToolConfig} This tool's configuration object
     * @param onChangeRuntimeConfig {function} A callback to be called when runtimeConfig is changed
     */
    constructor( config,  onChangeRuntimeConfig) {
        super(config.getCode(),  onChangeRuntimeConfig);
        this.config = config;
        this.onActivateCallbacks = new Set();
        this.onDeactivateCallbacks = new Set();
    }

    getClassName(){
        return "Tool";
    }

    getToolProperty(propCode) {
        return tumbo.runtimeConfig.getToolProperty(this.getCode(),propCode)
    }

    setToolProperty(propCode, value){
        tumbo.runtimeConfig.setToolProperty(this.getCode(), propCode, value);
        return this;
    }

    addHelpElementForTool() {
        this.addHelpElement(new HelpElement("TOOL_" + this.getCode(), this.getXOffsetForHelpElement(), this.getYOffsetForHelpElement(), this.getUIElement()));
    }

    getXOffsetForHelpElement() {
        return -5;
    }

    getYOffsetForHelpElement() {
        return -8;
    }

    /**
     * Set the currently available layers
     * @param layers {Array<Layer>}
     */
    setLayers(layers) {
        this.layers = layers;
    }

    /**
     * Set all the available layers
     * @param allLayers {Array<Layer>}
     */
    setAllLayers(allLayers) {
        this.allLayers = allLayers;
    }

    /**
     * Set the available base layers
     * @param baseLayers {Array<BaseLayer>}
     */
    setBaseLayers(baseLayers) {
        this.baseLayers = baseLayers;
    }

    /**
     * Set the available context layers
     * @param contextLayers {Array<ContextLayer>}
     */
    setContextLayers(contextLayers) {
        this.contextLayers = contextLayers;
    }

    /**
     * Tool name comes from the config
     * @returns {string}
     */
    getName() {
        return this.config.getName();
    }

    /**
     * Unique tool code
     * @returns {string}
     */
    getCode() {
        return this.config.getCode();
    }

    /**
     * An array of OpenLayers controls to add to the map
     * @return {Array<Control>}
     */
    getOLControls() {
    }

    /**
     * An OpenLayers overlay to add to the map
     * @return {Overlay}
     */
    getOverlay() {
    }

    /**
     * Get a list of OL layers to be added by TumboMap on top of everything else
     * @return {Array.<Layer>}
     */
    getOLLayers(){
        return [];
    }

    /**
     * To be called by TumboMap when the visible layers change
     * @param visibleLayers {Array<Layer>} The currently visible layers
     */
    onChangeVisibleLayers(visibleLayers) {
    }

    /**
     * To be called by TumboMap when the visible base layers change
     * @param visibleBaseLayers {Array<BaseLayer>} The currently visible base layers
     */
    onChangeVisibleBaseLayers(visibleBaseLayers) {
    }

    /**
     * To be called by TumboMap when the visible context layers change
     * @param visibleContextLayers {Array<ContextLayer>} The currently visible context layers
     */
    onChangeVisibleContextLayers(visibleContextLayers) {
    }

    /**
     * To be called by TumboMap when there is navigation on the map
     * It should not be called more than 1 time every 500ms
     * @param zoomLevel {number} The new zoom level (integer)
     * @param centerWgs84 {Array<number>} The new center coordinates (WGS84)
     * @param extentWgs84 {Array<number>|undefined} The new map extent (WGS84)
     * @param resolution {Number|undefined} The new map resolution
     */
    onNavigation(zoomLevel, centerWgs84, extentWgs84, resolution) {
    }

    /**
     * To be called by DroughtMap when there is navigation on the map
     * @param zoomLevel {number} The new zoom level (integer)
     * @param centerWgs84 {Array<number>} The new center coordinates (WGS84)
     */
    onNavigationImmediately(zoomLevel, centerWgs84) {
    }

    /**
     * To be called by TumboMap when there a click on the map
     * @param coordinateWgs84 {Array<number>} The click coordinates (WGS84)
     * @param pixelX {number} The X pixel coordinate of the click (integer)
     * @param pixelY {number} The Y pixel coordinate of the click (integer)
     */
    onMapClick(coordinateWgs84, pixelX, pixelY) {
    }

    /**
     * Is this tool currently active?
     * @returns {boolean} true if it is
     */
    isActive() {
        return true;
    }

    /**
     * Activate this tool
     */
    activate() {}

    /**
     * Deactivate this tool
     */
    deactivate() {}

    onActivate(callback){
        this.onActivateCallbacks.add(callback);
        return this;
    }

    _runActivateCallbacks() {
        this.onActivateCallbacks.forEach(c=>c());
    }

    onDeactivate(callback){
        this.onDeactivateCallbacks.add(callback);
        return this;
    }

    _runDeactivateCallbacks() {
        this.onDeactivateCallbacks.forEach(c=>c());
    }

    /**
     * Override to implement whatever should happen when the tool is added to the map
     * @param map {Map} The openlayers map the tool will be handling
     */
    onAdd2Map(map) {
    }
}

/**
 * A tool that has a button that should be added to the map
 */
class ButtonTool extends Tool {

    /**
     *
     * @param config {ToolConfig} This tool's configuration object
     * @param onChangeRuntimeConfig {function} A callback to be called when runtimeConfig is changed
     * @param id {string} The id of the button container
     * @param buttonClass {string}  The HTML class of the button
     * @param onButtonClick {function} A callback to be called on button click
     */
    constructor( config, onChangeRuntimeConfig, id, buttonClass, onButtonClick) {
        super( config, onChangeRuntimeConfig);
        this.id = id;
        this.buttonClass = buttonClass;
        this.onButtonClick = onButtonClick;
    }

    getUIElement() {
        if (!this.uiElement) {
            this.uiElement = $('<div id="' + this.id + '" class="ol-unselectable ol-control ' + this.buttonClass + '"></div>');
            const toolButton = $('<button type="button" class="' + this.buttonClass + '" title="' + this.getName() + '"></button>');
            this.uiElement.append(toolButton);
            toolButton.css('pointer-events', 'auto');
            if (this.onButtonClick) {
                //this call strategy enables subclasses to pass the onClick callback in the constructor super call
                toolButton.click(() => this.onButtonClick.call(this));
            }
            this.onAdd2DOM(()=> this.addHelpElementForTool());
        }
        return this.uiElement;
    }

    getClassName(){
        return "ButtonTool";
    }
}

/**
 * A tool that has a button that should be added to the map
 * This tool's active state is toggled on button click
 */
class ActiveButtonTool extends ButtonTool {
    /**
     *
     * @param config {ToolConfig} This tool's configuration object
     * @param onChangeRuntimeConfig {function} A callback to be called when runtimeConfig is changed
     * @param id {string} The id of the button container
     * @param buttonClass {string}  The HTML class of the button
     */
    constructor( config, onChangeRuntimeConfig, id, buttonClass) {
        super( config, onChangeRuntimeConfig, id, buttonClass, function () {
            if (this.isActive()) {
                this.deactivate();
                this.logStat("DEACTIVATE");
            } else {
                this.activate();
                this.logStat("ACTIVATE");
            }
        });
    }

    getClassName(){
        return "ActiveButtonTool";
    }

    onMapClick(coordinateWgs84, pixelX, pixelY) {
        if (this.isActive()) {
            super.onMapClick(coordinateWgs84, pixelX, pixelY);
            this.logStat("MAP_CLICK");
        }
    }

    onAdd2Map(map) {
        super.onAdd2Map(map);
        if (this.getSessionProp("active") === 'true') {
            this.activate();
        } else {
            this.deactivate();
        }
    }

    isActive() {
        return this.getSessionProp("active") === 'true';
    }

    /**
     * Activate this tool
     */
    activate() {
        this.setSessionProp("active", "true");
        this.active = true;
        $($('button', this.getUIElement())[0]).addClass('active');
        this.getUIElement().addClass('active');
        this._runActivateCallbacks();
    }

    /**
     * Deactivate this tool
     */
    deactivate() {
        this.setSessionProp("active", "false");
        this.active = false;
        $($('button', this.getUIElement())[0]).removeClass('active');
        this.getUIElement().removeClass('active');
        this._runDeactivateCallbacks();
    }

    isActivated(){
        return this.active;
    }
}

/**
 * A tool that has a button that should be added to the map
 * This tool's active state is toggled on button click
 * When active, all tools of this type should be deactivated
 */
class ExclusiveButtonTool extends ActiveButtonTool {

    /**
     *
     * @param config {ToolConfig} This tool's configuration object
     * @param onChangeRuntimeConfig {function} A callback to be called when runtimeConfig is changed
     * @param id {string} The id of the button container
     * @param buttonClass {string}  The HTML class of the button
     * @param deactivateExclusiveToolsFunction {function} A callback to be called on activate. This shall deactivate all other exclusive tools
     */
    constructor( config, onChangeRuntimeConfig, id, buttonClass, deactivateExclusiveToolsFunction) {
        super( config, onChangeRuntimeConfig, id, buttonClass);
        this.deactivateExclusiveToolsFunction = deactivateExclusiveToolsFunction;
    }

    getClassName(){
        return "ExclusiveButtonTool";
    }

    activate() {
        if (this.deactivateExclusiveToolsFunction)
            this.deactivateExclusiveToolsFunction();
        super.activate();
    }
}


/**
 * A tool that has a panel
 */
class PanelTool extends Panel { //polymorphism is very hard to achieve in Javascript, so this is basically copying the class above, inheriting from a subclass

    /**
     *
     * @param config {ToolConfig} This tool's configuration object
     * @param id {string} Panel container id
     * @param startCollapsedIfSmall {boolean} If this map is on a small container, should the panel startup collapsed?
     * @param onChangeRuntimeConfig {function} A callback to be called when runtimeConfig is changed
     */
    constructor( config, id, startCollapsedIfSmall, onChangeRuntimeConfig) {
        super( id, startCollapsedIfSmall, onChangeRuntimeConfig);
        this.config = config;
        this.onActivateCallbacks = new Set();
        this.onDeactivateCallbacks = new Set();
    }

    getClassName(){
        return "PanelTool";
    }

    getToolProperty(propCode) {
        return tumbo.runtimeConfig.getToolProperty(this.getCode(),propCode)
    }

    setToolProperty(propCode, value){
        tumbo.runtimeConfig.setToolProperty(this.getCode(), propCode, value);
        return this;
    }

    addHelpElementForTool() {
        this.addHelpElement(new HelpElement("TOOL_" + this.getCode(), this.getXOffsetForHelpElement(), this.getYOffsetForHelpElement(), this.getUIElement()));
    }

    getXOffsetForHelpElement() {
        return 5;
    }

    getYOffsetForHelpElement() {
        return -2;
    }

    getUIElement() {
        if(!this.uiElement){
            const elem = super.getUIElement();
            this.onAdd2DOM(()=> this.addHelpElementForTool());
            return elem;
        }
        return super.getUIElement();
    }

    /**
     * Set the currently available layers
     * @param layers {Array<Layer>}
     */
    setLayers(layers) {
        this.layers = layers;
    }

    /**
     * Set all the available layers
     * @param allLayers {Array<Layer>}
     */
    setAllLayers(allLayers) {
        this.allLayers = allLayers;
    }

    /**
     * Set the available base layers
     * @param baseLayers {Array<BaseLayer>}
     */
    setBaseLayers(baseLayers) {
        this.baseLayers = baseLayers;
    }

    /**
     * Set the available context layers
     * @param contextLayers {Array<ContextLayer>}
     */
    setContextLayers(contextLayers) {
        this.contextLayers = contextLayers;
    }

    /**
     * Tool name comes from the config
     * @returns {string}
     */
    getName() {
        return this.config.getName();
    }

    /**
     * Unique tool code
     * @returns {string}
     */
    getCode() {
        return this.config.getCode();
    }

    /**
     * An array of OpenLayers controls to add to the map
     * @return {Array<Control>}
     */
    getOLControls() {
    }

    /**
     * An OpenLayers overlay to add to the map
     * @return {Overlay}
     */
    getOverlay() {
    }

    /**
     * Get a list of OL layers to be added by TumboMap on top of everything else
     * @return {Array.<Layer>}
     */
    getOLLayers(){
        return [];
    }

    /**
     * To be called by TumboMap when the visible layers change
     * @param visibleLayers {Layer} The currently visible layers
     */
    onChangeVisibleLayers(visibleLayers) {
    }

    /**
     * To be called by TumboMap when the visible base layers change
     * @param visibleBaseLayers {BaseLayer} The currently visible base layers
     */
    onChangeVisibleBaseLayers(visibleBaseLayers) {
    }

    /**
     * To be called by TumboMap when the visible context layers change
     * @param visibleContextLayers {ContextLayer} The currently visible context layers
     */
    onChangeVisibleContextLayers(visibleContextLayers) {
    }

    /**
     * To be called by TumboMap when there is navigation on the map
     * It should not be called more than 1 time every 500ms
     * @param zoomLevel {number} The new zoom level (integer)
     * @param centerWgs84 {Array<number>} The new center coordinates (WGS84)
     * @param extentWgs84 {Array<number>|undefined} The new map extent (WGS84)
     * @param resolution {Number|undefined} The new map resolution
     */
    onNavigation(zoomLevel, centerWgs84, extentWgs84, resolution) {
    }

    /**
     * To be called by TumboMap when there is navigation on the map
     * @param zoomLevel {number} The new zoom level (integer)
     * @param centerWgs84 {Array<number>} The new center coordinates (WGS84)
     */
    onNavigationImmediately(zoomLevel, centerWgs84) {
    }

    /**
     * To be called by TumboMap when there a click on the map
     * @param coordinateWgs84 {Array<number>} The click coordinates (WGS84)
     * @param pixelX {number} The X pixel coordinate of the click (integer)
     * @param pixelY {number} The Y pixel coordinate of the click (integer)
     */
    onMapClick(coordinateWgs84, pixelX, pixelY) {
    }

    /**
     * Is this tool currently active?
     * @returns {boolean} true if it is
     */
    isActive() {
        return true;
    }

    /**
     * Activate this tool
     */
    activate() {}

    /**
     * Deactivate this tool
     */
    deactivate() {}

    onActivate(callback){
        this.onActivateCallbacks.add(callback);
        return this;
    }

    _runActivateCallbacks() {
        this.onActivateCallbacks.forEach(c=>c());
    }

    onDeactivate(callback){
        this.onDeactivateCallbacks.add(callback);
        return this;
    }

    _runDeactivateCallbacks() {
        this.onDeactivateCallbacks.forEach(c=>c());
    }

    /**
     * Override to implement whatever should happen when the tool is added to the map
     * Make sure you call super.onAdd2Map()
     */
    onAdd2Map() {
        const target = $(this.getUIElement().parents('main.drought-map')[0]);
        const checkMapSmall = () => {
            const width = target.width();
            const height = target.height();
            if (width < 650) {
                this.smallWidthForPanel = true;
                target.addClass('small-tumbo-width-for-panel')
            } else{
                this.smallWidthForPanel = false;
                target.removeClass('small-tumbo-width-for-panel');
            }
            if (height < 710) {
                this.smallHeightForPanel = true;
                target.addClass('small-tumbo-height-for-panel')
            } else {
                this.smallHeightForPanel = false;
                target.removeClass('small-tumbo-height-for-panel');
            }
        };
        new ResizeObserver(checkMapSmall).observe(target[0]);
        checkMapSmall();
    }

    isSmallContainerHeightForPanel(){
        return !!this.smallHeightForPanel;
    }

    isSmallContainerWidthForPanel(){
        return !!this.smallWidthForPanel;
    }
}

export {Tool, ButtonTool, ActiveButtonTool, ExclusiveButtonTool, PanelTool}
